import { HttpClient } from '@angular/common/http';
import { Injectable, WritableSignal } from '@angular/core';
import { forkJoin, map, Observable, of } from 'rxjs';

import { environment } from '../../../environments/environment';
import { Student, StudentAdapter } from '../models/student.model';
import { SchoolClass } from '../models/school-class.model';

import { SessionStorageService } from './session-storage.service';
import { EnrollmentService } from './enrollment.service';
import { UtilityService } from './utility.service';

export interface SubscriptionSummary {
  // NULL TeacherID, NULL StudentID (not visible to teacher users)
  unassigned: number;
  // NULL StudentID (this number is all teacher count for admin/support and just the individual teacher for a teacher user)
  teacher: number;
  // Consumed = 0
  unconsumed: number;
  // Consumed = 1
  consumed: number;
  unlimited: boolean;
}

export interface AssignDiagnosticsResolvedData {
  students: Student[];
  grades: string[];
  classes: SchoolClass[];
  diagnosticLicenseCount: number;
  interventionLicenseCount: number;
  teacherLicenseAvailabilityById: Record<number, { diagnosticLicenseCount: number, interventionLicenseCount: number }>;
}

interface FetchStudentOptions {
  schoolID?: number;
  teacherID?: number;
}

interface TeacherSubscriptionAvailability {
  teacherID: number;
  diagnosticLicenseCount: number;
  interventionLicenseCount: number;
}

type TeacherSubscriptionUpdate = {
  [subscriptionType in "diagnostic" | "system"]: { [teacherID: number]: number };
}

const REQ_OPTS = {
  withCredentials: true
};

@Injectable({
  providedIn: 'root'
})
export class AssignDiagnosticService {
  constructor(
    private httpClient: HttpClient,
    private sessionStorage: SessionStorageService,
    private studentAdapter: StudentAdapter,
    private enrollmentService: EnrollmentService,
    private utilityService: UtilityService,
  ) { }

  getResolvedPageData(): Observable<AssignDiagnosticsResolvedData> {
    const currentUser = this.sessionStorage.userData();
    if (!currentUser) {
      return of({
        students: [],
        grades: [ 'All' ],
        classes: [{
          name: 'All',
          schoolClassID: 0,
          teacherID: 0,
          createDate: (new Date()),
          status: ''
        }],
        diagnosticLicenseCount: 0,
        interventionLicenseCount: 0,
        teacherLicenseAvailabilityById: {},
        teachers: [],
        selectedTeacherId: 0,
      });
    }

    if (!currentUser.isSchoolAdmin()) {
      // Non-school admin user: return teacher data for the current user
      return this.getTeacherPageData();
    }

    // School admin user: default to returning data for the first teacher in the school
    return this.getTeacherPageData({
      teacherID: this.sessionStorage.getSchoolAdminTeacherFilter()?.selectedTeacherId,
      schoolID: currentUser.schoolID,
    });
  }

  /**
   * Gets teacher data for the Subscriptions page. If no teacherID or schoolID is provided,
   * the IDs associated with the current user are used.
   */
  getTeacherPageData(params?: { teacherID?: number | null, schoolID?: number | null }): Observable<AssignDiagnosticsResolvedData> {
    const currentUser = this.sessionStorage.userData();
    if (!currentUser) {
      return of({
        students: [],
        grades: [ 'All' ],
        classes: [{
          name: 'All',
          schoolClassID: 0,
          teacherID: 0,
          createDate: (new Date()),
          status: ''
        }],
        diagnosticLicenseCount: 0,
        interventionLicenseCount: 0,
        teacherLicenseAvailabilityById: {},
      });
    }

    const selectedTeacherID = params?.teacherID ?? currentUser.userID;
    const selectedSchoolID = params?.schoolID ?? currentUser.schoolID;

    const isAllTeachersView = selectedTeacherID === 0;
    const studentsRequest = this.getStudents(isAllTeachersView ? {schoolID: selectedSchoolID} : {teacherID: selectedTeacherID});

    const availabilityRequest = isAllTeachersView
      ? this.enrollmentService.getSchoolTeacherSubscriptionAvailability(selectedSchoolID).pipe(
          map((rows) => this.mapTeacherLicenseAvailability(rows))
        )
      : forkJoin({
          diagnosticLicenseCount: this.enrollmentService.getRemainingDiagnosticLicensesCount(selectedTeacherID, selectedSchoolID),
          interventionLicenseCount: this.enrollmentService.getRemainingInterventionLicensesCount(selectedTeacherID, selectedSchoolID),
        }).pipe(
          map((counts) => ({
            [selectedTeacherID]: {
              diagnosticLicenseCount: counts.diagnosticLicenseCount,
              interventionLicenseCount: counts.interventionLicenseCount,
            }
          }))
        );

    return forkJoin({
      students: studentsRequest,
      teacherLicenseAvailabilityById: availabilityRequest,
    }).pipe(
      map((results) => {
        const students = this.formatStudentsForManageSubscriptions(results.students || []);
        const filterOptions = this.buildFilterOptions(students);
        const selectedTeacherAvailability = results.teacherLicenseAvailabilityById[selectedTeacherID];

        return {
          students,
          grades: filterOptions.grades,
          classes: filterOptions.classes,
          diagnosticLicenseCount: selectedTeacherAvailability?.diagnosticLicenseCount ?? 0,
          interventionLicenseCount: selectedTeacherAvailability?.interventionLicenseCount ?? 0,
          teacherLicenseAvailabilityById: results.teacherLicenseAvailabilityById,
        };
      })
    );
  }

  updateTeacherGrantedSubscriptions(schoolID: number, updates: TeacherSubscriptionUpdate) {
    return this.httpClient.put<void>(`${environment.dataServicesURL}manage/enroll/school/${schoolID}/teachers/subscriptions/update`, updates, REQ_OPTS);
  }

  /**
   * Fetches a list of students with subscription data for either the entire school or a specific teacher
   *
   * @param filter If filter.teacherID is set, list will be filtered by that teacher.
   *   If filter.schoolID is set and filter.teacherID is not set, list will be filtered by that school.
   * @returns An array of students with their subscription data
   */
  getStudents(filter: FetchStudentOptions): Observable<Student[]> {
    const selectedSchoolID = filter.schoolID && filter.schoolID > 0 ? filter.schoolID : null;
    const selectedTeacherID = filter.teacherID && filter.teacherID > 0
      ? filter.teacherID
      : null;

    let endpoint = `${environment.dataServicesURL}manage/enroll/managesubscriptions`;
    if (selectedSchoolID != null && selectedTeacherID == null) {
      endpoint += `/school/${selectedSchoolID}/students`;
    } else if (selectedTeacherID != null) {
      endpoint += `/teacher/${selectedTeacherID}/students`;
    } else {
      throw new Error('Either teacherID or schoolID must be set when calling getStudents()');
    }

    return this.httpClient.get<Student[]>(endpoint, REQ_OPTS).pipe(
      map((data: any) => {
        let students: Student[] = [];

        data.forEach((student: any) => {
          students.push(this.studentAdapter.adapt(student));
        });

        return students;
      })
    );
  }

  getSubscriptionsForStudents(studentIDs: number[]) {
    return this.httpClient
      .get<any[]>(`${environment.dataServicesURL}manage/enroll/students/${studentIDs.join(",")}/subscriptions`, REQ_OPTS)
      .pipe(
        map(subList => {
          const subs: {
            [studentID: number]: {
              diagnostic?: { subscriptionID: number; consumed: boolean };
              system?: { subscriptionID: number; consumed: boolean };
            }
          } = {};
          for (const sub of subList) {
            if (!subs[sub.studentId]) {
              subs[sub.studentId] = {}
            }
            subs[sub.studentId][sub.order.subscriptionsType === 1 ? "diagnostic" : "system"] = {
              subscriptionID: sub.subscriptionId,
              consumed: sub.consumed
            };
          }
          return subs;
        })
      );

  }

  assignDiagnosticToStudent(studentID: number, teacherID?: number) {
    const user = this.sessionStorage.userData();
    const selectedTeacherID = teacherID ?? user?.userID;

    return this.httpClient.put(`${environment.dataServicesURL}manage/enroll/students/assigndiagnostic/add/${selectedTeacherID}/${studentID}?subscriptionSource=${this.subscriptionPool()}`, {}, REQ_OPTS);
  }

  assignDiagnosticToStudents(studentIDs: number[], teacherID?: number) {
    const user = this.sessionStorage.userData();
    const selectedTeacherID = teacherID ?? user?.userID;

    return this.httpClient.put(`${environment.dataServicesURL}manage/enroll/students/subscriptions/diagnostic/assign/${selectedTeacherID}/${studentIDs.join(',')}?subscriptionSource=${this.subscriptionPool()}`, {}, REQ_OPTS);
  }

  assignFullProductToStudents(studentIDs: number[], teacherID?: number) {
    const user = this.sessionStorage.userData();
    const selectedTeacherID = teacherID ?? user?.userID;

    return this.httpClient.put(`${environment.dataServicesURL}manage/enroll/students/subscriptions/intervention/assign/${selectedTeacherID}/${studentIDs.join(',')}?subscriptionSource=${this.subscriptionPool()}`, {}, REQ_OPTS);
  }

  assignFullProductToStudent(studentID: number, teacherID?: number){
    const user = this.sessionStorage.userData()
    const selectedTeacherID = teacherID ?? user?.userID;

    return this.httpClient.put(`${environment.dataServicesURL}manage/enroll/students/assignintervention/add/${selectedTeacherID}/${studentID}?subscriptionSource=${this.subscriptionPool()}`, {}, REQ_OPTS)

  }

  removeFullProductFromStudent(studentID: number, subscriptionID: number){

    return this.httpClient.put(`${environment.dataServicesURL}manage/enroll/students/assignintervention/remove/${studentID}/${subscriptionID}?subscriptionSource=${this.subscriptionPool()}`, {}, REQ_OPTS);
  }

  removeFullProductFromStudents(studentIDs: number[], teacherID?: number) {
    const user = this.sessionStorage.userData();
    const selectedTeacherID = teacherID ?? user?.userID;

    return this.httpClient.put(`${environment.dataServicesURL}manage/enroll/students/subscriptions/intervention/remove/${selectedTeacherID}/${studentIDs.join(',')}?subscriptionSource=${this.subscriptionPool()}`, {}, REQ_OPTS);
  }

  removeDiagnosticFromStudent(studentID: number, subscriptionID: number){
    return this.httpClient.put(`${environment.dataServicesURL}manage/enroll/students/assigndiagnostic/remove/${studentID}/${subscriptionID}?subscriptionSource=${this.subscriptionPool()}`, {}, REQ_OPTS);
  }

  removeDiagnosticFromStudents(studentIDs: number[], teacherID?: number) {
    const user = this.sessionStorage.userData();
    const selectedTeacherID = teacherID ?? user?.userID;

    return this.httpClient.put(`${environment.dataServicesURL}manage/enroll/students/subscriptions/diagnostic/remove/${selectedTeacherID}/${studentIDs.join(',')}?subscriptionSource=${this.subscriptionPool()}`, {}, REQ_OPTS);
  }

  getSchoolSubscriptionSummary(schoolID: number) {
    return this.httpClient.get<{[sub in "diagnostic" | "system"]: SubscriptionSummary }>(`${environment.dataServicesURL}manage/enroll/school/${schoolID}/SubscriptionSummary`, REQ_OPTS);
  }

  getNumSubscriptionsRemaining(schoolID: number, teacherID?: number) {
    return this.httpClient.get<{[sub in "diagnostic" | "system"]: number}>(`${environment.dataServicesURL}manage/enroll/school/${schoolID}/teachers/${teacherID ?? 0}/NumberUnassignedSubscriptions`, REQ_OPTS);
  }

  private formatStudentsForManageSubscriptions(students: Student[]): Student[] {
    students.forEach((student: Student) => {
      let showAddTestButton: boolean = true;
      let showRemoveACButton: boolean = true;

      student.assessments?.forEach((assessment) => {
        if (assessment.dateStarted) {
          assessment.formattedDateStarted = this.utilityService.formatDateNum(assessment.dateStarted);
        }

        if (assessment.isDiagnostic) {
          assessment.testWindow = this.utilityService.getTestWindow(assessment.dateStarted);
        }

        if (assessment.isAssessmentExpired()) {
          assessment.formattedDateCompleted = 'Expired';
        } else if (assessment.dateCompleted !== null) {
          assessment.formattedDateCompleted = this.utilityService.formatDateNum(assessment.dateCompleted);
        } else if (assessment.state.toLowerCase() === 'started') {
          assessment.formattedDateCompleted = 'In Progress';

          if (assessment.isDiagnostic || assessment.isPreDiagnostic || assessment.isMidDiagnostic || assessment.isPostDiagnostic) {
            let percentComplete = (assessment.numDiagnosticTasks === 0)
              ? 0
              : Math.round((assessment.numDiagnosticTasksCompleted / assessment.numDiagnosticTasks) * 100);
            if (assessment.skipped) {
              assessment.formattedDateCompleted = `Completed - ${percentComplete}%`;
            } else {
              assessment.formattedDateCompleted += ` - ${percentComplete}%`;
            }
          }
        } else if (assessment.state.toLowerCase() === 'assigned') {
          assessment.formattedDateCompleted = '';
        }

        if (showAddTestButton) {
          if (!assessment.skipped && !assessment.isAssessmentExpired() && (assessment.dateCompleted === null)) {
            showAddTestButton = false;
          } else if (assessment.isDiagnostic && assessment.isCompletedInWindow(this.utilityService.getTestWindow((new Date()).getTime()))) {
            showAddTestButton = false;
          }
        }

        if (showRemoveACButton && !assessment.isDiagnostic && !assessment.isScreener && assessment.dateStarted !== null) {
          showRemoveACButton = false;
        }

        student.showAddTestButton = showAddTestButton;
        student.showRemoveACButton = showRemoveACButton;
      });
    });

    return students;
  }

  private buildFilterOptions(students: Student[]): { grades: string[]; classes: SchoolClass[] } {
    const gradesSet = new Set<string>();
    students.forEach((student) => {
      gradesSet.add(this.utilityService.getStudentGrade(student.grade));
    });

    const grades = [ 'All' ].concat(this.utilityService.sortGrades(Array.from(gradesSet)));
    const classes = [{
      name: 'All',
      schoolClassID: 0,
      teacherID: 0,
      createDate: (new Date()),
      status: ''
    }].concat(this.utilityService.sortClasses(this.utilityService.getUniqueSchoolClasses(students)));

    return { grades, classes };
  }

  private mapTeacherLicenseAvailability(availability: TeacherSubscriptionAvailability[]): Record<number, { diagnosticLicenseCount: number, interventionLicenseCount: number }> {
    return availability.reduce((result, row) => {
      result[row.teacherID] = {
        diagnosticLicenseCount: row.diagnosticLicenseCount,
        interventionLicenseCount: row.interventionLicenseCount,
      };

      return result;
    }, {} as Record<number, { diagnosticLicenseCount: number, interventionLicenseCount: number }>);
  }

  private subscriptionPool() {
    const user = this.sessionStorage.userData()
    return (user?.isCustomerServiceUser() || user?.isSchoolAdmin()) ? 'admin' : 'teacher';
  }
}
